import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ShieldAlert, MapPin, Users, AlertTriangle, Zap, TrendingDown, ArrowUpRight, Brain } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const tip = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };
const COLORS = ['#1F3A34', '#9C9488', '#D9D3C8', '#285047', '#A67C52', '#5A5A5A'];
const sColor = (v, good, warn) => v >= good ? '#2E6F57' : v >= warn ? '#A67C52' : '#8A3C3C';

function monthsUntil(d) { if (!d) return 999; return Math.round((new Date(d) - new Date()) / (30.44*24*60*60*1000)); }

export default function RiskOverview() {
  const { assets: ctxAssets, loading: pfLoading } = usePortfolio();
  const [assets, setAssets] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState('base');

  useEffect(() => {
    (async () => {
      try {
        const { data: pfs } = await api.get('/portfolios');
        if (pfs.length > 0) {
          const { data: pf } = await api.get(`/portfolios/${pfs[0].portfolio_id}`);
          setAssets(pf.assets || []);
        }
        const { data: cts } = await api.get('/contracts');
        setContracts(cts);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const intel = useMemo(() => {
    if (!assets.length) return null;
    const totalRent = assets.reduce((s, a) => s + (a.annual_rent || 0), 0);
    const totalValue = assets.reduce((s, a) => s + (a.metrics?.market_value || a.market_value || 0), 0);
    const totalCf = assets.reduce((s, a) => s + (a.metrics?.net_cashflow || 0), 0);

    // Segments
    const segments = {};
    assets.forEach(a => { const t = (a.property_type || 'residential'); segments[t] = (segments[t] || 0) + (a.annual_rent || 0); });
    const segData = Object.entries(segments).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: Math.round(v), pct: totalRent > 0 ? Math.round(v / totalRent * 100) : 0 }));

    // Cities
    const cities = {};
    assets.forEach(a => { const c = a.city || 'Unknown'; cities[c] = (cities[c] || 0) + (a.metrics?.market_value || a.market_value || 0); });
    const cityData = Object.entries(cities).map(([k, v]) => ({ name: k, value: Math.round(v), pct: totalValue > 0 ? Math.round(v / totalValue * 100) : 0 })).sort((a, b) => b.value - a.value);
    const topCityPct = cityData[0]?.pct || 0;

    // Tenant concentration from contracts
    const tenantRent = {};
    contracts.forEach(c => { const t = c.tenant_name || 'Unknown'; tenantRent[t] = (tenantRent[t] || 0) + (c.monthly_rent || 0) * 12; });
    const tenantData = Object.entries(tenantRent).map(([k, v]) => ({ name: k, value: Math.round(v), pct: totalRent > 0 ? +(v / totalRent * 100).toFixed(1) : 0 })).sort((a, b) => b.value - a.value);
    const top1 = tenantData[0]?.pct || 0;
    const top3 = tenantData.slice(0, 3).reduce((s, t) => s + t.pct, 0);
    const top5 = tenantData.slice(0, 5).reduce((s, t) => s + t.pct, 0);
    const hhi = tenantData.reduce((s, t) => s + Math.pow(t.pct, 2), 0);

    // Vacancy
    const avgVacancy = assets.reduce((s, a) => s + (a.vacancy_rate || 0), 0) / Math.max(assets.length, 1);
    const vacStressImpact = totalRent * 0.05;

    // WALT + Expiry
    const enrichedContracts = contracts.map(c => ({ ...c, mo: monthsUntil(c.end_date) }));
    const totalMonthlyRent = contracts.reduce((s, c) => s + (c.monthly_rent || 0), 0);
    const walt = totalMonthlyRent > 0 ? enrichedContracts.reduce((s, c) => s + Math.max(c.mo, 0) * (c.monthly_rent || 0), 0) / totalMonthlyRent / 12 : 0;

    // Expiry by year
    const expiryYears = {};
    enrichedContracts.forEach(c => { const yr = c.end_date?.substring(0, 4) || '?'; if (!expiryYears[yr]) expiryYears[yr] = { year: yr, rent: 0, count: 0 }; expiryYears[yr].rent += (c.monthly_rent || 0) * 12; expiryYears[yr].count += 1; });
    const expiryData = Object.values(expiryYears).sort((a, b) => a.year.localeCompare(b.year));
    const totalAnnual = totalMonthlyRent * 12;
    const maxExpiryPct = totalAnnual > 0 ? Math.max(...expiryData.map(e => e.rent / totalAnnual * 100)) : 0;
    const expiryWallYear = expiryData.reduce((w, e) => e.rent > (w?.rent || 0) ? e : w, null);

    // Risk scores
    const tenantConcScore = Math.min(30, Math.round(top1 * 0.5 + top3 * 0.2));
    const geoSpreadScore = Math.min(25, Math.round(topCityPct * 0.3));
    const sectorMixScore = Math.min(20, segData.length === 1 ? 20 : segData.length === 2 ? 12 : 5);
    const leaseScore = Math.min(15, Math.round(Math.max(0, 15 - walt * 3)));
    const vacScore = Math.min(10, Math.round(avgVacancy * 3));
    const totalRiskScore = tenantConcScore + geoSpreadScore + sectorMixScore + leaseScore + vacScore;
    const diversificationScore = 100 - totalRiskScore;

    // Macro sensitivity
    const macroSensitivity = Math.round(tenantConcScore * 0.4 + geoSpreadScore * 0.3 + sectorMixScore * 0.3);

    // Monte Carlo simulation (simplified)
    const mcRuns = 200;
    const mcResults = [];
    for (let i = 0; i < mcRuns; i++) {
      const rentShock = 1 + (Math.random() - 0.55) * 0.15;
      const vacShock = avgVacancy + Math.random() * 5;
      const costShock = 1 + Math.random() * 0.05;
      const simCf = totalCf * rentShock * (1 - vacShock / 100) / (1 - avgVacancy / 100) / costShock;
      mcResults.push(simCf);
    }
    mcResults.sort((a, b) => a - b);
    const mcCfNeg = mcResults.filter(r => r < 0).length / mcRuns * 100;
    const mcWorst10 = mcResults[Math.floor(mcRuns * 0.1)];
    const mcExpectedLoss = Math.abs(Math.min(0, mcResults.reduce((s, r) => s + Math.min(0, r), 0) / mcRuns));
    const mcDist = [];
    const mcMin = mcResults[0], mcMax = mcResults[mcResults.length - 1], mcRange = mcMax - mcMin;
    for (let b = 0; b < 20; b++) {
      const lo = mcMin + (b / 20) * mcRange, hi = mcMin + ((b + 1) / 20) * mcRange;
      mcDist.push({ bucket: `${(lo/1e3).toFixed(0)}k`, count: mcResults.filter(r => r >= lo && r < hi).length, lo, hi });
    }

    // Scenario impacts
    const scenarioImpacts = {
      base: { cf: totalCf, label: 'Base Case' },
      tenant: { cf: totalCf - (tenantData[0]?.value || 0), label: 'Top Tenant Default' },
      vacancy: { cf: totalCf * (1 - 0.05 / (1 - avgVacancy / 100)), label: 'Vacancy +5%' },
      rent: { cf: totalCf * 0.9, label: 'Rent Downturn -10%' },
      combined: { cf: totalCf * 0.88 * (1 - 0.03 / (1 - avgVacancy / 100)), label: 'Combined Stress' },
    };

    // Risk-adjusted yield
    const netYield = totalValue > 0 ? totalCf / totalValue * 100 : 0;
    const volatility = totalRiskScore / 100 * 8 + 2;
    const sharpe = volatility > 0 ? netYield / volatility : 0;

    return {
      totalRiskScore, diversificationScore, avgVacancy, vacStressImpact, top1, top3, top5, hhi,
      tenantConcScore, geoSpreadScore, sectorMixScore, leaseScore, vacScore,
      maxExpiryPct, expiryWallYear, walt, macroSensitivity,
      segData, cityData, tenantData, expiryData,
      mcCfNeg, mcWorst10, mcExpectedLoss, mcDist,
      scenarioImpacts, netYield, volatility, sharpe,
      totalCf, totalRent, totalValue, topCityPct,
    };
  }, [assets, contracts]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;
  if (!intel) return <div className="text-center py-20 text-[#8C877F]">No data available.</div>;

  const activeScenario = intel.scenarioImpacts[scenario];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="risk-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Risk & Diversification Intelligence</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Portfolio Risk Desk</h1>
        </div>
        <div className="flex gap-1.5">
          {Object.entries(intel.scenarioImpacts).map(([id, s]) => (
            <button key={id} onClick={() => setScenario(id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-sans font-medium transition-all ${scenario === id ? 'bg-[#1F3A34] text-white' : 'bg-[#F4F1EB] text-[#5A5A5A] hover:bg-[#ECE7DE]'}`}>{s.label}</button>
          ))}
        </div>
      </div>

      {/* Risk Score + Breakdown + Improvement */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 flex flex-col items-center justify-center" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="relative w-32 h-32">
            <svg viewBox="0 0 120 120" className="w-full h-full">
              <circle cx="60" cy="60" r="52" fill="none" stroke="#F0EDE8" strokeWidth="8" />
              <circle cx="60" cy="60" r="52" fill="none" stroke={intel.totalRiskScore < 30 ? '#2E6F57' : intel.totalRiskScore < 50 ? '#A67C52' : '#8A3C3C'} strokeWidth="8"
                strokeDasharray={`${2*Math.PI*52}`} strokeDashoffset={`${2*Math.PI*52*(1-intel.totalRiskScore/100)}`}
                strokeLinecap="round" transform="rotate(-90 60 60)" className="transition-all duration-1000" />
              <text x="60" y="56" textAnchor="middle" fill="#1C1C1C" fontSize="28" fontFamily="Inter, system-ui" fontWeight="600">{intel.totalRiskScore}</text>
              <text x="60" y="72" textAnchor="middle" fill="#8C877F" fontSize="10" fontFamily="Inter, system-ui">RISK /100</text>
            </svg>
          </div>
          <p className="text-[14px] font-sans font-semibold mt-2" style={{ color: intel.totalRiskScore < 30 ? '#2E6F57' : intel.totalRiskScore < 50 ? '#A67C52' : '#8A3C3C' }}>
            {intel.totalRiskScore < 30 ? 'Low Risk' : intel.totalRiskScore < 50 ? 'Moderate' : 'Elevated'}
          </p>
          <p className="text-[12px] text-[#8C877F] mt-1">Diversification: {intel.diversificationScore}/100</p>
        </div>

        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Risk Breakdown</p>
          {[
            { l: 'Tenant Concentration', pts: intel.tenantConcScore, max: 30 },
            { l: 'Geographic Spread', pts: intel.geoSpreadScore, max: 25 },
            { l: 'Sector Mix', pts: intel.sectorMixScore, max: 20 },
            { l: 'Lease Duration', pts: intel.leaseScore, max: 15 },
            { l: 'Vacancy Volatility', pts: intel.vacScore, max: 10 },
          ].map(s => (
            <div key={s.l} className="flex items-center gap-3">
              <span className="text-[12px] text-[#5A5A5A] font-sans w-36">{s.l}</span>
              <div className="flex-1 h-2 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full" style={{ width: `${s.pts/s.max*100}%`, backgroundColor: s.pts/s.max >= 0.6 ? '#8A3C3C' : s.pts/s.max >= 0.4 ? '#A67C52' : '#2E6F57' }} /></div>
              <span className="text-[12px] font-sans font-semibold w-10 text-right">{s.pts}/{s.max}</span>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Improvement Potential</p>
          {[
            { action: `Reduce top tenant < 25%`, impact: '-6', newScore: Math.max(0, intel.totalRiskScore - 6) },
            { action: `Add city (reduce ${intel.cityData[0]?.name} < 60%)`, impact: '-4', newScore: Math.max(0, intel.totalRiskScore - 4) },
            { action: 'Add commercial segment', impact: '-5', newScore: Math.max(0, intel.totalRiskScore - 5) },
            { action: `Extend WALT > 4 years`, impact: '-3', newScore: Math.max(0, intel.totalRiskScore - 3) },
          ].map(s => (
            <div key={s.action} className="flex items-center justify-between py-1.5 border-b border-[#F4F1EB] last:border-0">
              <span className="text-[12px] text-[#5A5A5A] font-sans">{s.action}</span>
              <div className="flex items-center gap-2">
                <TrendingDown className="w-3 h-3 text-[#2E6F57]" />
                <span className="text-[12px] font-sans font-semibold text-[#2E6F57]">{s.newScore}</span>
                <span className="text-[10px] text-[#2E6F57] bg-[#E6EFEA] px-1.5 py-0.5 rounded">{s.impact}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Intelligence KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
        {[
          { l: 'WALT', v: `${intel.walt.toFixed(1)} yr`, c: sColor(intel.walt, 3, 1.5) },
          { l: 'Expiry Wall', v: `${intel.maxExpiryPct.toFixed(0)}%`, c: sColor(100-intel.maxExpiryPct, 60, 40) },
          { l: 'Vacancy Stress', v: fmt(intel.vacStressImpact), c: '#8A3C3C' },
          { l: 'HHI Index', v: Math.round(intel.hhi).toString(), c: sColor(10000-intel.hhi, 8000, 5000) },
          { l: 'Macro Sensitivity', v: `${intel.macroSensitivity}/100`, c: sColor(100-intel.macroSensitivity, 70, 50) },
          { l: 'Sharpe Ratio', v: intel.sharpe.toFixed(2), c: sColor(intel.sharpe, 1.0, 0.5) },
        ].map(k => (
          <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[10px] text-[#8C877F] font-sans font-medium mb-1">{k.l}</p>
            <p className="text-[22px] font-sans font-semibold leading-none" style={{ color: k.c }}>{k.v}</p>
          </div>
        ))}
      </div>

      {/* Scenario Impact Bar */}
      {scenario !== 'base' && (
        <div className="bg-[#FDF5F5] border border-[#8A3C3C]/15 rounded-xl p-5 flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-3"><AlertTriangle className="w-5 h-5 text-[#8A3C3C]" /><div><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">{activeScenario.label}</p><p className="text-[12px] text-[#8C877F]">Scenario impact on portfolio cashflow</p></div></div>
          <div className="flex items-center gap-6">
            <div><p className="text-[10px] text-[#8C877F]">Base CF</p><p className="text-[16px] font-sans font-semibold text-[#1C1C1C]">{fmt(intel.totalCf)}/yr</p></div>
            <div><p className="text-[10px] text-[#8C877F]">Stressed CF</p><p className="text-[16px] font-sans font-semibold text-[#8A3C3C]">{fmt(activeScenario.cf)}/yr</p></div>
            <div><p className="text-[10px] text-[#8C877F]">Impact</p><p className="text-[16px] font-sans font-semibold text-[#8A3C3C]">{fmt(activeScenario.cf - intel.totalCf)}</p></div>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Segment */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Segment Mix</p>
          <div className="flex items-center gap-6">
            <ResponsiveContainer width={130} height={130}>
              <PieChart><Pie data={intel.segData} cx="50%" cy="50%" innerRadius={38} outerRadius={58} dataKey="value" strokeWidth={0}>
                {intel.segData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie></PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {intel.segData.map((s, i) => (
                <div key={s.name} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                  <span className="text-[12px] text-[#1C1C1C] font-sans">{s.name}</span>
                  <span className="text-[12px] font-sans font-semibold ml-auto">{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* City */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Geographic Allocation</p>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={intel.cityData} layout="vertical">
              <CartesianGrid stroke="#E3DED6" horizontal={false} />
              <XAxis type="number" tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1e6).toFixed(1)}M`} />
              <YAxis type="category" dataKey="name" tick={{ fill: '#5A5A5A', fontSize: 11 }} axisLine={false} tickLine={false} width={80} />
              <Tooltip contentStyle={tip} formatter={v => [fmt(v), 'Value']} />
              <Bar dataKey="value" fill="#1F3A34" barSize={14} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Expiry Profile */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Expiry Wall</p>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={intel.expiryData}>
              <CartesianGrid stroke="#E3DED6" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: '#5A5A5A', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1e3).toFixed(0)}k`} />
              <Tooltip contentStyle={tip} formatter={v => [fmt(v), 'Rent']} />
              <Bar dataKey="rent" barSize={28} radius={[4, 4, 0, 0]}>
                {intel.expiryData.map((e, i) => {
                  const pct = intel.totalRent > 0 ? e.rent / intel.totalRent * 100 : 0;
                  return <Cell key={i} fill={pct > 30 ? '#8A3C3C' : pct > 20 ? '#A67C52' : '#1F3A34'} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tenant Concentration */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#1F3A34]" /><p className="text-[12px] text-[#8C877F] font-sans font-medium">Tenant Concentration Analysis</p></div>
          <div className="flex gap-4 text-[12px] font-sans">
            <span className="text-[#8C877F]">Top 1: <strong className={`${intel.top1 > 30 ? 'text-[#8A3C3C]' : 'text-[#1C1C1C]'}`}>{intel.top1.toFixed(0)}%</strong></span>
            <span className="text-[#8C877F]">Top 3: <strong className={`${intel.top3 > 60 ? 'text-[#8A3C3C]' : 'text-[#1C1C1C]'}`}>{intel.top3.toFixed(0)}%</strong></span>
            <span className="text-[#8C877F]">Top 5: <strong>{intel.top5.toFixed(0)}%</strong></span>
            <span className="text-[#8C877F]">HHI: <strong>{Math.round(intel.hhi)}</strong></span>
          </div>
        </div>
        {intel.tenantData.slice(0, 5).map((t, i) => (
          <div key={i} className="flex items-center justify-between py-2.5 border-b border-[#F4F1EB] last:border-0">
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-sans font-semibold text-[#8C877F] w-5">{i + 1}</span>
              <span className="text-[13px] text-[#1C1C1C] font-sans font-medium">{t.name}</span>
              {t.pct > 25 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#8A3C3C]/10 text-[#8A3C3C] font-medium">High</span>}
            </div>
            <div className="flex items-center gap-3 w-48">
              <span className="text-[12px] text-[#8C877F] font-sans">{fmt(t.value)}/yr</span>
              <div className="flex-1 h-2 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full" style={{ width: `${t.pct}%`, backgroundColor: t.pct > 30 ? '#8A3C3C' : t.pct > 20 ? '#A67C52' : '#1F3A34' }} /></div>
              <span className="text-[13px] font-sans font-semibold w-10 text-right">{t.pct}%</span>
            </div>
          </div>
        ))}
      </div>

      {/* Monte Carlo */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-4">Monte Carlo Simulation (200 runs)</p>
        <div className="grid grid-cols-4 gap-4 mb-5">
          {[
            { l: 'P(CF < 0)', v: `${intel.mcCfNeg.toFixed(0)}%`, c: sColor(100-intel.mcCfNeg, 95, 85) },
            { l: 'Expected Loss', v: fmt(intel.mcExpectedLoss), c: '#8A3C3C' },
            { l: 'Worst 10% Scenario', v: fmt(intel.mcWorst10), c: intel.mcWorst10 < 0 ? '#8A3C3C' : '#A67C52' },
            { l: 'Risk-Adj. Yield', v: `${intel.netYield.toFixed(1)}% / ${intel.volatility.toFixed(1)}σ`, c: '#1C1C1C' },
          ].map(k => (
            <div key={k.l}><p className="text-[10px] text-[#8C877F]">{k.l}</p><p className="text-[18px] font-sans font-semibold" style={{ color: k.c }}>{k.v}</p></div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={intel.mcDist}>
            <CartesianGrid stroke="#E3DED6" vertical={false} />
            <XAxis dataKey="bucket" tick={{ fill: '#8C877F', fontSize: 9 }} axisLine={false} tickLine={false} interval={3} />
            <YAxis tick={{ fill: '#8C877F', fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tip} formatter={(v, _, p) => [`${v} runs`, `Range: ${fmt(p.payload.lo)} — ${fmt(p.payload.hi)}`]} />
            <Bar dataKey="count" barSize={16} radius={[3, 3, 0, 0]}>
              {intel.mcDist.map((b, i) => <Cell key={i} fill={b.lo < 0 ? '#8A3C3C' : '#1F3A34'} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* AI Risk Actions */}
      <div className="bg-[#E6EFEA] border border-[#1F3A34]/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1F3A34] font-sans font-semibold">AI Risk Optimization Actions</p></div>
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Reduce <strong>{intel.tenantData[0]?.name}</strong> exposure (currently {intel.top1.toFixed(0)}%) by diversifying rental base → Risk Score improves by <strong className="text-[#2E6F57]">-6 points</strong>.
        </p>
        {intel.topCityPct > 60 && <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Portfolio {intel.topCityPct}% concentrated in {intel.cityData[0]?.name}. Adding assets in a new city reduces geographic risk by <strong className="text-[#2E6F57]">-4 points</strong>.
        </p>}
        {intel.expiryWallYear && <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Expiry wall in <strong>{intel.expiryWallYear.year}</strong> ({(intel.expiryWallYear.rent / intel.totalRent * 100).toFixed(0)}% of rent roll). Early renewal of 1 contract reduces wall by <strong className="text-[#2E6F57]">~12%</strong>.
        </p>}
        <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
          Monte Carlo: {intel.mcCfNeg.toFixed(0)}% probability of negative cashflow. Worst 10% scenario: {fmt(intel.mcWorst10)}/yr.
        </p>
      </div>
    </div>
  );
}
